import React from 'react';
import { 
  Modal, 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  Platform,
  Dimensions
} from 'react-native';
import { colors, typography, spacing, borderRadius } from '../constants/theme';
import { useTheme } from '../context/ThemeContext';

interface AlertDialogButton {
  text: string;
  onPress: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertDialogProps {
  visible: boolean;
  title: string;
  message?: string;
  buttons: AlertDialogButton[];
  onDismiss?: () => void;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  visible,
  title,
  message,
  buttons,
  onDismiss
}) => {
  const { theme } = useTheme();
  const { width } = Dimensions.get('window');
  const isSmallScreen = width < 375;

  // Get button style based on type
  const getButtonStyle = (style?: 'default' | 'cancel' | 'destructive') => {
    switch (style) {
      case 'destructive':
        return { backgroundColor: theme.colors.error };
      case 'cancel':
        return { backgroundColor: theme.colors.grey.medium };
      default:
        return { backgroundColor: theme.colors.primary };
    }
  };

  // Get button text style based on type
  const getButtonTextStyle = (style?: 'default' | 'cancel' | 'destructive') => {
    return { color: theme.colors.white };
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.overlay}>
        <View 
          style={[
            styles.dialogContainer, 
            { backgroundColor: theme.colors.card }
          ]}
        >
          <View style={styles.dialogHeader}>
            <Text 
              style={[
                styles.title, 
                { color: theme.colors.text },
                isSmallScreen && styles.smallScreenText
              ]}
            >
              {title}
            </Text>
          </View>

          {message && (
            <Text 
              style={[
                styles.message, 
                { color: theme.colors.text },
                isSmallScreen && styles.smallScreenText
              ]}
            >
              {message}
            </Text>
          )}

          <View style={[
            styles.buttonContainer,
            buttons.length > 2 && styles.buttonColumnContainer
          ]}>
            {buttons.map((button, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.button,
                  getButtonStyle(button.style),
                  buttons.length <= 2 && index < buttons.length - 1 && styles.buttonWithMargin,
                  buttons.length > 2 && styles.buttonColumn,
                  isSmallScreen && styles.smallScreenButton
                ]}
                onPress={() => {
                  button.onPress();
                  if (onDismiss) onDismiss();
                }}
              >
                <Text 
                  style={[
                    styles.buttonText, 
                    getButtonTextStyle(button.style),
                    isSmallScreen && styles.smallScreenText
                  ]}
                >
                  {button.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    ...(Platform.OS === 'web' ? {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    } : {})
  },
  dialogContainer: {
    width: '80%',
    maxWidth: 400,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.15)',
    } : {
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 4,
    })
  },
  dialogHeader: {
    marginBottom: spacing.md,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  message: {
    fontSize: typography.fontSize.md,
    marginBottom: spacing.lg,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-evenly',
  },
  buttonColumnContainer: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  button: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  buttonColumn: {
    marginVertical: spacing.xs / 2,
  },
  buttonWithMargin: {
    marginRight: spacing.md,
  },
  buttonText: {
    fontSize: typography.fontSize.md,
    fontWeight: '500',
  },
  smallScreenText: {
    fontSize: typography.fontSize.sm,
  },
  smallScreenButton: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
});

export default AlertDialog; 